import { api } from './http';
import type {
  MailAccount, MailLogRow, MailPurpose, OffsiteConfig, SmsConfig, SmsLogRow,
} from './types';

export const email = {
  load: (signal?: AbortSignal) => api.get<{
    accounts: MailAccount[];
    purposes: MailPurpose[];
    labels: Record<MailPurpose, string>;
    log: MailLogRow[];
    stats: { sent: number | null; pending: number | null; failed: number | null };
    can_store_passwords: boolean;
    subscribers: number;
  }>('/email', signal),
  /* A key absent from the body means "leave it", never "clear it" — which is
     what lets a password stay put while the host is edited. */
  save: (purpose: MailPurpose, body: Record<string, unknown>) =>
    api.put<{ account: MailAccount }>(`/email/${purpose}`, body),
  test: (purpose: MailPurpose, to: string) =>
    api.post<{ ok: boolean; message?: string }>('/email/test', { purpose, to }),
  retry: (id: number) => api.post<{ ok: true }>(`/email/log/${id}/retry`),
  newsletter: (subject: string, body: string) =>
    api.post<{ ok: true; queued: number }>('/email/newsletter', { subject, body }),
};

export type ReviewLogin = { enabled: boolean; mobile: string; code: string };

export const sms = {
  load: (signal?: AbortSignal) => api.get<{
    config: SmsConfig;
    log: SmsLogRow[];
    stats: { sent: number | null; pending: number | null; failed: number | null };
    can_store_secrets: boolean;
    configured: { otp: boolean; alerts: boolean };
  }>('/sms', signal),
  save: (body: Record<string, unknown>) => api.put<{ config: SmsConfig }>('/sms', body),
  test: (mobile: string) => api.post<{ ok: boolean; message?: string }>('/sms/test', { mobile }),
  reviewLogin: (signal?: AbortSignal) => api.get<ReviewLogin>('/sms/review-login', signal),
  setReviewLogin: (enabled: boolean) => api.put<ReviewLogin>('/sms/review-login', { enabled }),
};

export const offsite = {
  load: (signal?: AbortSignal) => api.get<{
    config: OffsiteConfig;
    enabled: boolean;
    missing: string[];
    can_store_secrets: boolean;
    objects: { key: string; size: number; uploaded: string }[];
    list_error: string;
  }>('/offsite', signal),
  save: (body: Record<string, unknown>) => api.put<{ config: OffsiteConfig }>('/offsite', body),
  test: () => api.post<{ ok: boolean; message?: string }>('/offsite/test'),
};

/* ── market news providers ─────────────────────────────────────────────── */

export interface NewsProvider {
  provider: 'drishti' | 'fmp';
  label: string;
  market: string;
  site: string;
  feeds: { key: string; label: string }[];
  has_key: boolean;
  key_readable: boolean;
  key_hint: string;
  enabled: boolean;
  members: boolean;
  licence_on_file: boolean;
  licence_ref: string;
  licence_date: string;
  options: {
    feeds: string[];
    important_only?: boolean;
    symbols?: string;
    countries?: string;
    min_impact?: 'Low' | 'Medium' | 'High';
  };
  tested_at: string | null;
  tested_ok: boolean;
  last_error: string;
  updated_at: string;
}

export interface NewsItem {
  title: string; summary: string; source: string; url: string; at: string; symbol: string; tag: string;
}

export const newsfeeds = {
  load: (signal?: AbortSignal) =>
    api.get<{ providers: NewsProvider[]; can_store_secrets: boolean }>('/newsfeeds', signal),
  save: (provider: string, body: Record<string, unknown>) =>
    api.put<{ provider: NewsProvider }>(`/newsfeeds/${provider}`, body),
  test: (provider: string) =>
    api.post<{ ok: boolean; items: NewsItem[]; provider: NewsProvider }>(`/newsfeeds/${provider}/test`, {}),
  preview: (provider: string, feed: string) =>
    api.get<{ items: NewsItem[] }>(`/newsfeeds/${provider}/preview?feed=${encodeURIComponent(feed)}`),
};
